import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatNumber } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { Person } from '../ui/Avatar';
import { useMembers } from '../ui/UserSelect';
import {
  completeSprint,
  createSprint,
  createTask,
  deleteSprint,
  getBacklog,
  listProjects,
  moveToSprint,
  startSprint,
  type BacklogItem,
  type Sprint,
} from './api';
import { SprintReportPanel } from './SprintReport';

/**
 * Planning: what is in the sprints that have not closed, and what is waiting (context.md §44-47).
 *
 * One screen for the conversation a team actually has — what goes in the next sprint, what comes
 * out of this one, what nobody has estimated. Items move with a select rather than by dragging:
 * it works on a phone, from a keyboard, and for somebody who cannot hold a mouse button down.
 *
 * A project that does not use sprints says so and shows nothing else, because for most teams that
 * is the honest screen.
 */
export function BacklogPage(): React.JSX.Element {
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });
  const chosen = params.get('project') ?? projects.data?.projects[0]?.id ?? '';

  const backlog = useQuery({
    queryKey: ['backlog', chosen],
    queryFn: () => getBacklog(chosen),
    enabled: chosen !== '',
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['backlog'] });
    void client.invalidateQueries({ queryKey: ['tasks'] });
    void client.invalidateQueries({ queryKey: ['sprint-report'] });
  };

  const pick = (projectId: string): void => {
    const next = new URLSearchParams(params);
    next.set('project', projectId);
    setParams(next, { replace: true });
  };

  const move = useMutation({
    mutationFn: (input: { taskId: string; sprintId: string | null }) =>
      moveToSprint(input.taskId, input.sprintId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const data = backlog.data;
  const sprints = data?.sprints ?? [];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('sprint.backlog')}
        description={t('sprint.backlogHint')}
        actions={
          <div className="cell-inline">
            <select
              className="select"
              value={chosen}
              onChange={(event) => pick(event.target.value)}
            >
              {(projects.data?.projects ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.key ? `${project.key} · ${project.name}` : project.name}
                </option>
              ))}
            </select>
            {data?.project.usesSprints && (
              <NewSprint projectId={chosen} onDone={refresh} onError={setError} />
            )}
          </div>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {projects.isLoading || backlog.isLoading ? (
        <Loading />
      ) : backlog.error ? (
        <ErrorState error={backlog.error} onRetry={() => void backlog.refetch()} />
      ) : !data ? (
        <Panel>
          <p className="muted">{t('sprint.noProject')}</p>
        </Panel>
      ) : !data.project.usesSprints ? (
        <Panel>
          <p className="muted">{t('sprint.offForProject')}</p>
          <p className="muted small">{t('sprint.offHint')}</p>
        </Panel>
      ) : (
        <>
          {sprints.map((sprint) => (
            <SprintSection
              key={sprint.id}
              sprint={sprint}
              siblings={sprints}
              onMove={(taskId, sprintId) => move.mutate({ taskId, sprintId })}
              onChanged={refresh}
              onError={setError}
              onReport={() => setReport(report === sprint.id ? null : sprint.id)}
              reportOpen={report === sprint.id}
            />
          ))}

          <Panel
            title={`${t('sprint.waiting')} · ${formatNumber(data.backlog.length)}`}
            actions={<NewItem projectId={chosen} onDone={refresh} onError={setError} />}
          >
            {data.backlog.length === 0 ? (
              <p className="muted">{t('sprint.backlogEmpty')}</p>
            ) : (
              <ItemList
                items={data.backlog}
                sprints={sprints}
                onMove={(taskId, sprintId) => move.mutate({ taskId, sprintId })}
              />
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

function stateTone(state: Sprint['state']): BadgeTone {
  return state === 'ACTIVE' ? 'positive' : state === 'COMPLETED' ? 'neutral' : 'info';
}

function SprintSection({
  sprint,
  siblings,
  onMove,
  onChanged,
  onError,
  onReport,
  reportOpen,
}: {
  sprint: Sprint & { items: BacklogItem[] };
  siblings: Sprint[];
  onMove: (taskId: string, sprintId: string | null) => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
  onReport: () => void;
  reportOpen: boolean;
}): React.JSX.Element {
  const [closing, setClosing] = useState(false);
  const [target, setTarget] = useState<string>('');

  const start = useMutation({
    mutationFn: () => startSprint(sprint.id),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const close = useMutation({
    mutationFn: () => completeSprint(sprint.id, target || null),
    onSuccess: () => {
      setClosing(false);
      onError(null);
      onChanged();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: () => deleteSprint(sprint.id),
    onSuccess: onChanged,
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const open = sprint.progress.items - sprint.progress.done - sprint.progress.dropped;
  const targets = siblings.filter((other) => other.id !== sprint.id && other.state !== 'COMPLETED');

  return (
    <Panel
      title={sprint.name}
      actions={
        <div className="cell-inline">
          <Badge tone={stateTone(sprint.state)}>{t(`sprint.state.${sprint.state}`)}</Badge>
          {sprint.state === 'ACTIVE' && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={onReport}
            >
              {t('sprint.report')}
            </button>
          )}
          {sprint.state === 'PLANNED' && (
            <>
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={start.isPending}
                onClick={() => start.mutate()}
              >
                {t('sprint.start')}
              </button>
              <button
                type="button"
                className="button button--quiet button--danger button--compact"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {t('social.delete')}
              </button>
            </>
          )}
          {sprint.state === 'ACTIVE' && (
            <button
              type="button"
              className="button button--compact"
              onClick={() => setClosing((current) => !current)}
            >
              {t('sprint.complete')}
            </button>
          )}
        </div>
      }
    >
      <p className="muted small">
        {sprint.goal ? `${sprint.goal} · ` : ''}
        {t('sprint.summary')
          .replace('{items}', String(sprint.progress.items))
          .replace('{done}', String(sprint.progress.done))
          .replace('{points}', String(sprint.progress.points))
          .replace('{pointsDone}', String(sprint.progress.pointsDone))}
        {sprint.progress.unestimated > 0 &&
          ` · ${t('sprint.unestimated').replace('{n}', String(sprint.progress.unestimated))}`}
        {sprint.startsAt && ` · ${formatDate(sprint.startsAt)}`}
        {sprint.endsAt && ` → ${formatDate(sprint.endsAt)}`}
      </p>

      {closing && (
        <div className="sprint-close">
          <p className="muted small">{t('sprint.closeHint').replace('{n}', String(open))}</p>
          <div className="cell-inline">
            <select
              className="select"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            >
              <option value="">{t('sprint.toBacklog')}</option>
              {targets.map((other) => (
                <option key={other.id} value={other.id}>
                  {t('sprint.toSprint').replace('{name}', other.name)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={close.isPending}
              onClick={() => close.mutate()}
            >
              {t('sprint.confirmClose')}
            </button>
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setClosing(false)}
            >
              {t('confirm.cancel')}
            </button>
          </div>
        </div>
      )}

      {reportOpen && <SprintReportPanel sprintId={sprint.id} />}

      {sprint.items.length === 0 ? (
        <p className="muted">{t('sprint.empty')}</p>
      ) : (
        <ItemList items={sprint.items} sprints={siblings} onMove={onMove} />
      )}
    </Panel>
  );
}

const typeTone: Record<BacklogItem['itemType'], BadgeTone> = {
  EPIC: 'info',
  STORY: 'positive',
  TASK: 'neutral',
  BUG: 'danger',
  SUBTASK: 'neutral',
};

function ItemList({
  items,
  sprints,
  onMove,
}: {
  items: BacklogItem[];
  sprints: Sprint[];
  onMove: (taskId: string, sprintId: string | null) => void;
}): React.JSX.Element {
  const { options: people } = useMembers();
  const open = sprints.filter((sprint) => sprint.state !== 'COMPLETED');

  return (
    <ul className="backlog">
      {items.map((item) => {
        const person = people.find((candidate) => candidate.id === item.assigneeId);

        return (
          <li key={item.id} className="backlog__item">
            <span className="backlog__main">
              <Badge tone={typeTone[item.itemType]}>{t(`work.item.${item.itemType}`)}</Badge>
              {item.key && <code className="backlog__key">{item.key}</code>}
              <Link to={`/trabalho/tarefas/${item.id}`} className="backlog__title">
                {item.title}
              </Link>
            </span>

            <span className="cell-inline">
              {item.storyPoints !== null && (
                <span className="points" title={t('sprint.points')}>
                  {item.storyPoints}
                </span>
              )}
              <Badge
                tone={
                  item.status === 'DONE'
                    ? 'positive'
                    : item.status === 'BLOCKED'
                      ? 'danger'
                      : 'neutral'
                }
              >
                {t(`work.task.${item.status}`)}
              </Badge>
              {person && <Person size="xs" name={person.name} url={person.avatarUrl} />}
              <select
                className="select select--compact"
                value={item.sprintId ?? ''}
                aria-label={t('sprint.moveTo')}
                onChange={(event) => onMove(item.id, event.target.value || null)}
              >
                <option value="">{t('sprint.waiting')}</option>
                {open.map((sprint) => (
                  <option key={sprint.id} value={sprint.id}>
                    {sprint.name}
                  </option>
                ))}
              </select>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function NewSprint({
  projectId,
  onDone,
  onError,
}: {
  projectId: string;
  onDone: () => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [starts, setStarts] = useState('');
  const [ends, setEnds] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createSprint({
        projectId,
        name: name.trim(),
        goal: goal.trim() || null,
        startsAt: starts ? new Date(`${starts}T09:00:00`).toISOString() : null,
        endsAt: ends ? new Date(`${ends}T18:00:00`).toISOString() : null,
      }),
    onSuccess: () => {
      setOpen(false);
      setName('');
      setGoal('');
      onError(null);
      onDone();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  if (!open) {
    return (
      <button
        type="button"
        className="button button--primary button--compact"
        onClick={() => setOpen(true)}
      >
        {t('sprint.new')}
      </button>
    );
  }

  return (
    <form
      className="cell-inline"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim()) create.mutate();
      }}
    >
      <input
        value={name}
        placeholder={t('sprint.name')}
        maxLength={120}
        autoFocus
        onChange={(event) => setName(event.target.value)}
      />
      <input
        value={goal}
        placeholder={t('sprint.goal')}
        maxLength={200}
        onChange={(event) => setGoal(event.target.value)}
      />
      <input
        type="date"
        value={starts}
        aria-label={t('sprint.starts')}
        onChange={(event) => setStarts(event.target.value)}
      />
      <input
        type="date"
        value={ends}
        aria-label={t('sprint.ends')}
        onChange={(event) => setEnds(event.target.value)}
      />
      <button
        type="submit"
        className="button button--primary button--compact"
        disabled={!name.trim() || create.isPending}
      >
        {t('sprint.create')}
      </button>
      <button
        type="button"
        className="button button--quiet button--compact"
        onClick={() => setOpen(false)}
      >
        {t('confirm.cancel')}
      </button>
    </form>
  );
}

function NewItem({
  projectId,
  onDone,
  onError,
}: {
  projectId: string;
  onDone: () => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<BacklogItem['itemType']>('TASK');
  const [points, setPoints] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createTask({
        title: title.trim(),
        projectId,
        itemType: type,
        storyPoints: points ? Number(points.replace(',', '.')) : null,
      }),
    onSuccess: () => {
      setTitle('');
      setPoints('');
      onError(null);
      onDone();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  return (
    <form
      className="cell-inline"
      onSubmit={(event) => {
        event.preventDefault();
        if (title.trim()) create.mutate();
      }}
    >
      <select
        className="select select--compact"
        value={type}
        onChange={(event) => setType(event.target.value as BacklogItem['itemType'])}
      >
        {(['STORY', 'TASK', 'BUG', 'EPIC'] as const).map((value) => (
          <option key={value} value={value}>
            {t(`work.item.${value}`)}
          </option>
        ))}
      </select>
      <input
        value={title}
        placeholder={t('sprint.itemTitle')}
        maxLength={300}
        onChange={(event) => setTitle(event.target.value)}
      />
      <input
        className="points-input"
        value={points}
        inputMode="decimal"
        placeholder={t('sprint.pointsShort')}
        onChange={(event) => setPoints(event.target.value)}
      />
      <button
        type="submit"
        className="button button--compact"
        disabled={!title.trim() || create.isPending}
      >
        {t('sprint.addItem')}
      </button>
    </form>
  );
}
